import { use, useCallback } from 'react';
import styled from 'styled-components';
import { Hymn } from '../../../common/src/model.ts';
import Add from '../assets/add.svg?react';
import Remove from '../assets/remove.svg?react';
import { setSongTranspositionForOrg, useCurrentOrg } from '../data/use_current_org.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { CurrentSongContext } from '../player/current_song.tsx';
import { showToast } from './toast.tsx';

const keys = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
// The printed key as stored: the list's twelve spellings, plus the three flats the importer prints that the list
// spells otherwise (Db, Gb, Cb). The importer's keyFromFifths (server/src/music/import/music_xml.ts) writes only these;
// an older or hand-edited song file can still hold anything else, which the row treats as an unrecognised key.
const keyIndexBySpelling = new Map([
  ...keys.map((key, index) => [key, index] as const),
  ...([
    ['Db', 1],
    ['Gb', 6],
    ['Cb', 11],
  ] as const),
]);

// The key heard at a total transposition, church plus device, in whole steps. Floored modulo, so a negative total
// wraps (Eb - 4 half steps = B, not clamped to C). A key that lands on the printed one, unmoved or an octave off, keeps
// the printed spelling (Db, not C#), as does one the list cannot name; any other takes the list's.
function keyHeardAt(printedKey: string | undefined, totalWholeSteps: number) {
  const printedIndex = keyIndexBySpelling.get(printedKey ?? '');
  if (printedIndex === undefined) {
    return printedKey;
  }
  const movedIndex = (((printedIndex + totalWholeSteps * 2) % 12) + 12) % 12;
  return movedIndex === printedIndex ? printedKey : keys[movedIndex];
}

// The song menu's Transpose Key row (issue #4221): the - / key / + control moves the key on this device only, Reset
// puts this device back, and an administrator's "Set to ... for church" (or school, or small group) saves the key
// heard for the whole organization. The header shows the printed key and the key heard whenever they differ.
export const TransposeKeyRow = ({ hymn }: { hymn: Hymn }) => {
  const { currentOrg, updateOrgInfo } = useCurrentOrg();
  const { isInternalUser, isOrgAdmin } = useUserAttributes();
  const { userKeyTransposition, setUserKeyTransposition } = use(CurrentSongContext);

  // Transpositions, the church's and this device's, count whole steps: one tap is half a step, 0.5
  const churchTransposition = currentOrg?.songTranspositions?.[hymn.slug] ?? 0;
  // False for a printed key the map does not hold: bad data, such as a lowercase letter ("g") or a stray word
  const isPrintedKeyKnown = keyIndexBySpelling.has(hymn.key ?? '');
  const totalTransposition = churchTransposition + userKeyTransposition;
  // Moved or not is decided on the half steps, not the key's name, so an octave off (F → F) still reads as moved
  const isMoved = isPrintedKeyKnown && totalTransposition !== 0;
  const keyHeard = keyHeardAt(hymn.key, totalTransposition);
  const hasDeviceOffset = userKeyTransposition !== 0;
  const maySetChurchKey = !!currentOrg && (isInternalUser || isOrgAdmin);
  // a cached org may lack the type
  const orgTypeLabel = currentOrg?.type?.toLowerCase() ?? 'church';

  const modulateKey = useCallback(
    (delta: number) => {
      setUserKeyTransposition(Math.min(6, Math.max(-6, userKeyTransposition + delta)));
    },
    [setUserKeyTransposition, userKeyTransposition],
  );

  const reset = useCallback(() => {
    setUserKeyTransposition(0);
  }, [setUserKeyTransposition]);

  const onSetSongKeyForOrganization = useCallback(async () => {
    if (!currentOrg) {
      return;
    }
    // The cached org holds the new key as soon as the save starts (the push to the server follows, or is queued while
    // offline), so the row goes quiet and confirms at once rather than after the round trip
    const saving = updateOrgInfo(
      setSongTranspositionForOrg({ org: currentOrg, songSlug: hymn.slug, modulationWholeSteps: totalTransposition }),
    );
    reset();
    // An unrecognised printed key gives no name for the key saved, so neither does the confirmation
    showToast(isPrintedKeyKnown ? `Set to ${keyHeard} for ${currentOrg.name}` : `Key set for ${currentOrg.name}`);
    await saving;
  }, [currentOrg, hymn.slug, isPrintedKeyKnown, keyHeard, reset, totalTransposition, updateOrgInfo]);

  return (
    <TransposeKeyWrapper>
      <Header>
        <SettingLabel>Transpose Key</SettingLabel>
        <HeaderEnd>
          {isMoved && (
            <KeyChange>
              {hymn.key} → {keyHeard}
            </KeyChange>
          )}
          <ResetButton $visible={hasDeviceOffset} onClick={reset}>
            Reset
          </ResetButton>
        </HeaderEnd>
      </Header>

      <ControlRow>
        <MinusEnd aria-label="Lower the key" onClick={() => modulateKey(-0.5)}>
          <Remove className="addSubtractIcon" />
        </MinusEnd>
        <KeyBox>{keyHeard}</KeyBox>
        <PlusEnd aria-label="Raise the key" onClick={() => modulateKey(0.5)}>
          <Add className="addSubtractIcon" />
        </PlusEnd>
        {maySetChurchKey && (
          <SetForChurchButton $visible={hasDeviceOffset} onClick={onSetSongKeyForOrganization}>
            <SetForChurchKey>{isPrintedKeyKnown ? `Set to ${keyHeard}` : 'Set key'}</SetForChurchKey>
            {/* not laid out (whitespace in a flex column), but it separates the two lines in the accessible name */}{' '}
            <SetForChurchScope>for {orgTypeLabel}</SetForChurchScope>
          </SetForChurchButton>
        )}
      </ControlRow>
    </TransposeKeyWrapper>
  );
};

// The sheet ends where this content does, so the padding keeps the transpose controls off the screen's edge;
// when they are hidden (a practice video), Song options' own bottom margin ends the sheet
const TransposeKeyWrapper = styled.div`
  padding-bottom: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 5px 20px;
`;

const SettingLabel = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
`;

const HeaderEnd = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const KeyChange = styled.div`
  color: var(--color-text-light);
`;

// Reset and the church-wide button hide by visibility, not by unmounting, while this device's offset comes and goes,
// so nothing shifts within a session. The church-wide button is not mounted at all for a member who may not set the
// church key, so their control sits centred.
const ResetButton = styled.button<{ $visible: boolean }>`
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 14px;
  color: var(--color-text-dangerous);
  background: transparent;
  border: 1.5px solid var(--color-text-dangerous);
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  cursor: pointer;
`;

const ControlRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  padding: 0 20px;
`;

// Real buttons, so they take focus and answer Enter and Space; index.css borders every button, hence border: none
const ControlEnd = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  border: none;
  padding: 0;
  margin: 0;
  background-color: var(--color-nightingale);
  width: 60px;
  height: 46px;

  .addSubtractIcon {
    width: 50px;
    height: 50px;
    /* the icons stroke with currentColor; --color-text-button flips white/black to contrast with the purple in each theme */
    color: var(--color-text-button);
  }
`;

const MinusEnd = styled(ControlEnd)`
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
`;

const PlusEnd = styled(ControlEnd)`
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
`;

const KeyBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  border-top: 3px solid var(--color-nightingale);
  border-bottom: 3px solid var(--color-nightingale);
  width: 80px;
`;

// A fixed width, so the row does not shift between a one- and a two-character key. The row, 60 + 80 + 60 + 14 + 100 =
// 314 px, fits a 360 px phone inside the sheet's paddings.
const SetForChurchButton = styled.button<{ $visible: boolean }>`
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  line-height: 1.15;
  background-color: var(--color-nightingale);
  color: var(--color-text-button);
  -webkit-appearance: none;
  appearance: none;
  border: none;
  width: 100px;
  height: 46px;
  padding: 0 6px;
  /* the -, key and + boxes join into one control, so only this button is spaced */
  margin-left: 14px;
  border-radius: 10px;
  cursor: pointer;
`;

const SetForChurchKey = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 14px;
`;

const SetForChurchScope = styled.span`
  font-family: Jost-Regular, Arial, sans-serif;
  font-size: 12px;
  opacity: 0.85;
`;
